"use client";

import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { useInternalVolumeAccess, InternalVolumeUnavailable } from "@/lib/useInternalVolumeAccess";

type Family = {
  signal_set: string;
  total_signals: number;
  days: number;
  avg_excess_vs_kse100_pct: number;
  avg_return_pct: number;
  hit_rate_vs_kse100_pct: number;
  confidence: "established" | "emerging" | "building";
};

type Scoreboard = {
  ok?: boolean;
  sessions: number;
  date_range: { start: string | null; end: string | null };
  total_signals_graded: number;
  benchmark: string;
  families: Family[];
};

const LOOP_STEPS = [
  "Snapshot every scanner's signals before fresh data arrives.",
  "Pull the new PSX session data.",
  "Compare each signal against the actual next-session move.",
  "Measure performance versus KSE100 — not just whether it was green.",
  "Roll results up per signal family across multiple sessions.",
  "Promote what consistently works; down-weight what doesn't.",
];

const confidenceStyle = (c: Family["confidence"]): CSSProperties => {
  const base: CSSProperties = {
    display: "inline-block",
    padding: "0.1rem 0.5rem",
    borderRadius: "999px",
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };
  if (c === "established")
    return { ...base, background: "rgba(16,185,129,0.16)", color: "#34d399", border: "1px solid rgba(16,185,129,0.35)" };
  if (c === "emerging")
    return { ...base, background: "rgba(96,165,250,0.14)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.32)" };
  return { ...base, background: "rgba(148,163,184,0.12)", color: "var(--color-text-muted)", border: "1px solid rgba(148,163,184,0.25)" };
};

export default function ScoreboardClient() {
  const { allowed, checked, token } = useInternalVolumeAccess();
  const [data, setData] = useState<Scoreboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!checked || !allowed || !token) return;
    const controller = new AbortController();
    fetch("/api/outputs/signal_learning_scoreboard.json", {
      cache: "no-store",
      signal: controller.signal,
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Request failed (${r.status})`);
        return r.json();
      })
      .then((d) => setData(d))
      .catch((e) => {
        if (!controller.signal.aborted) setError(e instanceof Error ? e.message : "Failed to load");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [checked, allowed, token]);

  if (!checked) {
    return <div style={{ textAlign: "center", padding: "2rem", color: "var(--color-text-muted)" }}>Checking access…</div>;
  }
  if (!allowed) return <InternalVolumeUnavailable />;

  const established = data?.families.some((f) => f.confidence === "established");

  return (
    <section style={{ maxWidth: "920px", margin: "0 auto", padding: "1.5rem 1rem 3rem" }}>
      {/* How the loop works */}
      <div style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: "12px", padding: "1.25rem 1.5rem", marginBottom: "1.5rem" }}>
        <h2 style={{ margin: "0 0 0.75rem", fontSize: "1.05rem", color: "#3b82f6" }}>How the learning loop works</h2>
        <ol style={{ margin: 0, paddingLeft: "1.2rem", color: "var(--color-text-muted)", lineHeight: 1.7, fontSize: "0.92rem" }}>
          {LOOP_STEPS.map((s) => <li key={s}>{s}</li>)}
        </ol>
      </div>

      {loading && <p style={{ color: "var(--color-text-muted)", padding: "1rem" }}>Loading scoreboard…</p>}
      {error && <p style={{ color: "var(--color-danger)", padding: "1rem" }}>Couldn&apos;t load the scoreboard right now ({error}).</p>}

      {data && data.families && data.families.length > 0 && (
        <>
          {/* Meta line */}
          <p style={{ color: "var(--color-text-muted)", marginBottom: "0.5rem", fontSize: "0.92rem" }}>
            Graded <strong style={{ color: "var(--color-text)" }}>{data.total_signals_graded.toLocaleString()}</strong> long
            signals across <strong style={{ color: "var(--color-text)" }}>{data.sessions}</strong> sessions
            {data.date_range.start && data.date_range.end ? ` (${data.date_range.start} → ${data.date_range.end})` : ""}.
            Benchmark: <strong style={{ color: "var(--color-text)" }}>{data.benchmark}</strong>.
          </p>

          {/* Honesty callout */}
          {!established && (
            <div style={{ background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.22)", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1.25rem", fontSize: "0.88rem", color: "var(--color-text-muted)", lineHeight: 1.6 }}>
              Early validation: every family is still <strong>“emerging”</strong> or <strong>“building”</strong> — none has enough
              sessions yet to be called established. The point isn&apos;t any single number; it&apos;s that the measurement runs every day and the sample grows.
            </div>
          )}

          <div style={{ overflowX: "auto", borderRadius: "10px", border: "1px solid rgba(148,163,184,0.14)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
              <thead>
                <tr style={{ background: "rgba(59,130,246,0.08)" }}>
                  {[
                    ["Signal family", "left"],
                    ["Sample", "right"],
                    ["Avg excess vs KSE100", "right"],
                    ["Hit rate vs KSE100", "right"],
                    ["Confidence", "right"],
                  ].map(([label, align]) => (
                    <th key={label} style={{ padding: "0.7rem 0.9rem", textAlign: align as "left" | "right", fontWeight: 600, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)", whiteSpace: "nowrap", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.families.map((f, idx) => {
                  const dim = f.confidence === "building";
                  const exc = f.avg_excess_vs_kse100_pct;
                  return (
                    <tr key={f.signal_set} style={{ background: idx % 2 === 0 ? "transparent" : "rgba(59,130,246,0.04)", borderBottom: "1px solid rgba(148,163,184,0.08)", opacity: dim ? 0.6 : 1 }}>
                      <td style={{ padding: "0.6rem 0.9rem", fontWeight: 600, color: "var(--color-text)" }}>{f.signal_set}</td>
                      <td style={{ padding: "0.6rem 0.9rem", textAlign: "right", color: "var(--color-text-muted)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {f.total_signals.toLocaleString()} · {f.days}d
                      </td>
                      <td style={{ padding: "0.6rem 0.9rem", textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: exc > 0 ? "var(--color-success)" : exc < 0 ? "var(--color-danger)" : "var(--color-text-muted)" }}>
                        {exc > 0 ? "+" : ""}{exc.toFixed(2)}%
                      </td>
                      <td style={{ padding: "0.6rem 0.9rem", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--color-text-muted)" }}>
                        {f.hit_rate_vs_kse100_pct.toFixed(0)}%
                      </td>
                      <td style={{ padding: "0.6rem 0.9rem", textAlign: "right" }}>
                        <span style={confidenceStyle(f.confidence)}>{f.confidence}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p style={{ marginTop: "1.5rem", fontSize: "0.82rem", color: "var(--color-text-muted)", lineHeight: 1.6 }}>
            <strong>Confidence</strong> reflects sample size and sessions observed: <em>established</em> (10+ sessions, 50+ signals),
            <em> emerging</em> (3+ sessions, 20+ signals), <em>building</em> (below that — read with caution). “Avg excess vs KSE100”
            is the average next-session return above the index, weighted by signal count.
          </p>

          <p style={{ marginTop: "1rem", fontSize: "0.8rem", color: "var(--color-text-muted)", lineHeight: 1.6 }}>
            Research and education only. Past performance does not guarantee future results. This is not financial advice and not a buy or sell recommendation.
          </p>
        </>
      )}
    </section>
  );
}
